import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, BACKEND } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { TrendingUp, DollarSign, ShoppingCart, Wallet, Target, AlertTriangle, Download, PackageX, ChevronRight, Receipt, CalendarDays } from "lucide-react";

const COLORS = ["hsl(160 84% 39%)", "hsl(0 84% 60%)"];

const PRESETS = [
  { key: "hoy", label: "Hoy" },
  { key: "ayer", label: "Ayer" },
  { key: "semana", label: "Esta semana" },
  { key: "semana_pasada", label: "Semana pasada" },
  { key: "mes", label: "Este mes" },
  { key: "mes_anterior", label: "Mes anterior" },
  { key: "ytd", label: "Año en curso" },
];

const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(key) {
  const now = new Date();
  const mk = (y, m, day) => new Date(y, m, day);
  const today = mk(now.getFullYear(), now.getMonth(), now.getDate());
  const mondayOffset = (today.getDay() + 6) % 7;
  let from = today, to = today;
  if (key === "ayer") { from = new Date(today); from.setDate(today.getDate() - 1); to = new Date(from); }
  else if (key === "semana") { from = new Date(today); from.setDate(today.getDate() - mondayOffset); to = today; }
  else if (key === "semana_pasada") { const mon = new Date(today); mon.setDate(today.getDate() - mondayOffset); to = new Date(mon); to.setDate(mon.getDate() - 1); from = new Date(to); from.setDate(to.getDate() - 6); }
  else if (key === "mes") { from = mk(now.getFullYear(), now.getMonth(), 1); to = today; }
  else if (key === "mes_anterior") { from = mk(now.getFullYear(), now.getMonth() - 1, 1); to = mk(now.getFullYear(), now.getMonth(), 0); }
  else if (key === "ytd") { from = mk(now.getFullYear(), 0, 1); to = today; }
  return { desde: toYMD(from), hasta: toYMD(to) };
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 border-border rounded-lg hover:-translate-y-1 hover:shadow-md transition-transform" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</span>
        <Icon className={`w-5 h-5 ${accent || "text-interactive"}`} />
      </div>
      <div className="text-2xl font-heading font-bold tracking-tight">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState(() => presetRange("mes"));
  const [preset, setPreset] = useState("mes");
  const [calRange, setCalRange] = useState(undefined);
  const [calOpen, setCalOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard", { params: { desde: range.desde, hasta: range.hasta } }).then((r) => setD(r.data));
  }, [range.desde, range.hasta]);

  const applyPreset = (key) => { setPreset(key); setCalRange(undefined); setRange(presetRange(key)); };
  const applyCustom = (sel) => {
    setCalRange(sel);
    if (sel?.from && sel?.to) {
      setPreset("custom");
      setRange({ desde: toYMD(sel.from), hasta: toYMD(sel.to) });
      setCalOpen(false);
    }
  };

  const exportar = () => { window.open(`${BACKEND}/api/dashboard/export?desde=${range.desde}&hasta=${range.hasta}`, "_blank"); };
  const goCobranza = () => document.getElementById("cxc")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const rangoLabel = `${fmtDate(range.desde)} — ${fmtDate(range.hasta)}`;
  if (!d) return <div className="p-8 text-muted-foreground">Cargando panel...</div>;
  const k = d.kpis;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Panel Financiero</h1>
          <p className="text-muted-foreground text-sm" data-testid="dash-range-label">Periodo: {rangoLabel} · MXN</p>
        </div>
        <Button variant="outline" onClick={exportar} data-testid="export-report"><Download className="w-4 h-4 mr-2" /> Reporte Excel</Button>
      </div>

      <Card className="p-3 rounded-lg" data-testid="dashboard-filters">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm" variant={preset === p.key ? "default" : "outline"}
              onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`}>{p.label}</Button>
          ))}
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant={preset === "custom" ? "default" : "outline"} data-testid="preset-custom">
                <CalendarDays className="w-4 h-4 mr-1.5" /> {preset === "custom" ? rangoLabel : "Personalizado"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end" data-testid="custom-range-popover">
              <Calendar mode="range" selected={calRange} onSelect={applyCustom} numberOfMonths={2} defaultMonth={new Date(`${range.desde}T00:00:00`)} />
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {/* Resumen compacto (solo móvil) */}
      <div className="md:hidden space-y-4" data-testid="mobile-dash-summary">
        <Card className="p-4 rounded-lg">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Resumen del periodo</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-muted/50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1"><DollarSign className="w-3 h-3 text-emerald-600" /> Ingresos</div>
              <div className="text-lg font-heading font-bold text-emerald-700">{fmtMXN(k.ingresos_reales)}</div>
            </div>
            <div className="rounded-md bg-muted/50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Ganancia</div>
              <div className={`text-lg font-heading font-bold ${k.ganancia_neta >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtMXN(k.ganancia_neta)}</div>
            </div>
            <div className="rounded-md bg-muted/50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Ventas</div>
              <div className="text-lg font-heading font-bold">{fmtMXN(k.ventas_totales)}</div>
            </div>
            <div className="rounded-md bg-red-50 border border-red-100 p-3">
              <div className="text-[10px] uppercase tracking-wide text-red-500 mb-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Vencidas</div>
              <div className="text-lg font-heading font-bold text-red-600">{fmtMXN(d.cuentas_por_cobrar.vencidas)}</div>
            </div>
          </div>
        </Card>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="default" className="h-14 justify-between" onClick={() => nav("/app/pedidos")} data-testid="quick-pedidos">
            <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Pedidos</span>
            <ChevronRight className="w-4 h-4 opacity-70" />
          </Button>
          <Button variant="secondary" className="h-14 justify-between" onClick={goCobranza} data-testid="quick-cobranza">
            <span className="flex items-center gap-2"><Receipt className="w-4 h-4" /> Cobranza</span>
            <ChevronRight className="w-4 h-4 opacity-70" />
          </Button>
        </div>
      </div>

      <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi icon={ShoppingCart} label="Ventas Totales" value={fmtMXN(k.ventas_totales)} />
        <Kpi icon={DollarSign} label="Ingresos Reales" value={fmtMXN(k.ingresos_reales)} accent="text-emerald-600" />
        <Kpi icon={Wallet} label="Gastos Compras" value={fmtMXN(k.gastos_compras)} accent="text-red-500" />
        <Kpi icon={TrendingUp} label="Ganancia Neta" value={fmtMXN(k.ganancia_neta)} accent={k.ganancia_neta >= 0 ? "text-emerald-600" : "text-red-500"} />
        <Kpi icon={Target} label="Pipeline" value={fmtMXN(k.pipeline)} accent="text-amber-500" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-lg lg:col-span-1">
          <h3 className="font-heading font-semibold mb-4">Ingresos vs Gastos</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={d.pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {d.pie.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMXN(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 rounded-lg lg:col-span-2" id="cxc">
          <h3 className="font-heading font-semibold mb-4">Cuentas por Cobrar</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <div className="text-xs uppercase tracking-wide text-red-500 font-medium mb-1">Vencidas</div>
              <div className="text-2xl font-heading font-bold text-red-600" data-testid="cxc-vencidas">{fmtMXN(d.cuentas_por_cobrar.vencidas)}</div>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="text-xs uppercase tracking-wide text-emerald-600 font-medium mb-1">En Tiempo</div>
              <div className="text-2xl font-heading font-bold text-emerald-700">{fmtMXN(d.cuentas_por_cobrar.en_tiempo)}</div>
            </div>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {d.overdue.length === 0 && <p className="text-sm text-muted-foreground">Sin pedidos vencidos. 🎉</p>}
            {d.overdue.map((o) => (
              <div key={o.folio} className="flex items-center justify-between text-sm p-2 rounded bg-red-50 border border-red-100" data-testid="overdue-row">
                <span className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-4 h-4" /> {o.folio} · {o.cliente}</span>
                <span className="font-medium text-red-700">{fmtMXN(o.saldo)} · vence {fmtDate(o.vencimiento)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {d.reorder_alerts.length > 0 && (
        <Card className="p-6 rounded-lg border-amber-300">
          <h3 className="font-heading font-semibold mb-4 flex items-center gap-2 text-amber-600"><PackageX className="w-5 h-5" /> Alertas de Reorden</h3>
          <div className="space-y-2">
            {d.reorder_alerts.map((p) => (
              <div key={p.sku} className="flex items-center justify-between text-sm p-2 rounded bg-amber-50 border border-amber-100">
                <span>{p.nombre} [{p.sku}] · stock {p.stock_actual} / mín {p.stock_minimo}</span>
                <span className="flex gap-2">{p.proveedor_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="text-interactive underline">Comprar {i + 1}</a>)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
